// FunctionsPOC.js — Documentation Fonctionnelle : POC « Fonctions » (module Environnement).
// Aperçu interactif de l'écran réel de gestion des Fonctions : en-tête dégradé (compteur, bascule
// table/grille, rafraîchir, nouvelle fonction) + barre recherche & filtres (Environnement, Statut, Type,
// Domaine) + liste table/grille + tiroir de détail à droite (StatCards, Détails techniques, badges,
// callout Environnement, MD5, Métadonnées) + formulaire de création modal à 2 onglets.
// Source de vérité : automata-studio-frontend [AUT][03]-Functions.js + automata_environment FunctionController.
// Les actions sont SIMULÉES localement (démo, aucun appel réseau).
import React, { useState } from 'react';
import {
  Plus, RefreshCw, Code, FileCode, Package, Tag, Globe, Hash, Zap, Layers, Shield, Star,
  CheckCircle, XCircle, Edit2, Trash2, X, Save, Info, ArrowRight, Server,
  CalendarDays, User, Clock, List, Grid, Brain, ExternalLink,
} from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle, Button, Badge, Tabs, Icon, SearchBar, EmptyState } from '../ui';

// Domaines & niveaux : champs libres côté backend (valeurs distinctes en base). Exemples pour la démo.
const DOMAINS = ['CREDIT', 'DEBIT', 'COMPENSATION', 'REPORTING', 'PARAMETRAGE'];
const TYPES = ['PROGRAM', 'MENU', 'FUNCTION', 'REPORT', 'BATCH', 'SERVICE', 'API', 'WEB', 'INCLUDE', 'LIBRARY'];
const LEVELS = [
  { value: 'basic', label: 'Basique' },
  { value: 'intermediate', label: 'Intermédiaire' },
  { value: 'advanced', label: 'Avancé' },
  { value: 'expert', label: 'Expert' },
];
const levelLabel = (v) => LEVELS.find((l) => l.value === v)?.label || v || 'N/A';

const ENVIRONMENTS = ['BGFI-Recette', 'BGFI-Production'];

const SAMPLE = [
  { id: 101, functionName: 'Saisie virement', functionDescription: 'Saisie d\'un ordre de virement client', functionProgram: 'VIRSAISIE', functionType: 'PROGRAM', functionDomain: 'CREDIT', functionModule: 'VIREMENTS', level: 'intermediate', priority: 2, isActive: true, isSpecific: false, usedByClient: true, environment: { appName: 'BGFI-Recette', appType: 'GENERO_GDC', appVersion: 'v11' }, md5Key: 'a1b2c3d4e5f6', createdBy: 'admin', createdDate: '2026-06-10', modifiedBy: 'admin', modifiedDate: '2026-06-14', lastExecutionDate: '2026-06-14' },
  { id: 102, functionName: 'Consultation solde', functionDescription: 'Affichage du solde d\'un compte', functionProgram: 'CPTSOLDE', functionType: 'MENU', functionDomain: 'REPORTING', functionModule: 'COMPTES', level: 'basic', priority: 4, isActive: true, isSpecific: false, usedByClient: false, environment: { appName: 'BGFI-Recette', appType: 'GENERO_GDC', appVersion: 'v11' }, md5Key: 'f6e5d4c3b2a1', createdBy: 'admin', createdDate: '2026-06-09', modifiedBy: 'admin', modifiedDate: '2026-06-11', lastExecutionDate: null },
  { id: 103, functionName: 'Batch compensation', functionDescription: 'Traitement de compensation nocturne', functionProgram: 'BATCHCOMP', functionType: 'BATCH', functionDomain: 'COMPENSATION', functionModule: null, level: 'expert', priority: 1, isActive: false, isSpecific: true, usedByClient: true, environment: null, md5Key: '9988776655', createdBy: 'ops', createdDate: '2026-06-08', modifiedBy: 'ops', modifiedDate: '2026-06-12', lastExecutionDate: '2026-06-12' },
];

const EMPTY_FORM = {
  functionName: '', functionDescription: '', functionProgram: '', functionModule: '',
  functionType: '', functionDomain: '', level: 'basic', priority: 3,
  isActive: true, isSpecific: false, usedByClient: false, environmentName: '',
};

const priorityColor = (p) => (p <= 2 ? '#10B981' : p <= 4 ? '#F59E0B' : '#EF4444');

// ---- Petits sous-composants de la réplique ---------------------------------
// Pastille de statut (point vert pulsé / gris) — réplique du StatusDot de l'écran.
const StatusDot = ({ active }) => (
  <span className="relative flex items-center justify-center">
    {active && <span className="absolute w-2 h-2 rounded-full bg-emerald-400 animate-ping opacity-40" />}
    <span className={`relative w-2 h-2 rounded-full ${active ? 'bg-emerald-400' : 'bg-gray-400 dark:bg-gray-600'}`} />
  </span>
);

// StatCard du tiroir : icône + libellé + valeur, fond dégradé léger — réplique de l'écran.
const StatCard = ({ icon: IconCmp, label, value }) => (
  <div className="rounded-xl bg-gray-50 dark:bg-gray-700/50 p-3.5 border border-gray-200/60 dark:border-gray-700/40">
    <div className="flex items-center gap-3">
      <div className="flex-shrink-0 p-2 rounded-lg bg-white/70 dark:bg-gray-800/60 text-blue-600 dark:text-blue-400"><IconCmp className="h-4 w-4" /></div>
      <div className="min-w-0">
        <p className="text-[11px] font-medium text-gray-500 dark:text-gray-400 truncate">{label}</p>
        <p className="text-sm font-semibold text-gray-900 dark:text-gray-100 truncate">{value || 'N/A'}</p>
      </div>
    </div>
  </div>
);

// Carte titrée du tiroir (en-tête uppercase + lignes icône/libellé/valeur) — réplique de l'écran.
const DetailCard = ({ title, rows }) => (
  <div className="rounded-xl border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800 overflow-hidden">
    <div className="px-4 py-2.5 border-b border-gray-100 dark:border-gray-700">
      <h4 className="text-xs font-semibold text-gray-900 dark:text-white uppercase tracking-wider">{title}</h4>
    </div>
    <div className="divide-y divide-gray-100 dark:divide-gray-700">
      {rows.map(([Ic, label, value], i) => (
        <div key={i} className="flex items-center gap-3 px-4 py-2.5">
          <Ic className="h-3.5 w-3.5 text-gray-400 flex-shrink-0" />
          <span className="text-xs text-gray-500 w-28 flex-shrink-0">{label}</span>
          <span className="text-xs font-medium text-gray-900 dark:text-white truncate">{value || '-'}</span>
        </div>
      ))}
    </div>
  </div>
);

const GuideStep = ({ n, children }) => (
  <li className="flex items-start gap-3">
    <span className="shrink-0 w-6 h-6 rounded-full bg-blue-500 text-white text-xs font-bold flex items-center justify-center">{n}</span>
    <span className="text-sm text-gray-700 dark:text-gray-300">{children}</span>
  </li>
);

// ---- Page -------------------------------------------------------------------
const FunctionsPOC = () => {
  const [functions, setFunctions] = useState(SAMPLE);
  const [selectedId, setSelectedId] = useState(null);
  const [view, setView] = useState('table'); // 'table' | 'grid'
  const [showCreate, setShowCreate] = useState(false);
  const [formTab, setFormTab] = useState('base'); // 'base' | 'advanced'
  const [search, setSearch] = useState('');
  const [envFilter, setEnvFilter] = useState('all');
  const [statusFilter, setStatusFilter] = useState('all'); // 'all' | 'active' | 'inactive'
  const [typeFilter, setTypeFilter] = useState('all');
  const [domainFilter, setDomainFilter] = useState('all');
  const [form, setForm] = useState(EMPTY_FORM);
  const [toast, setToast] = useState('');

  const selected = functions.find((f) => f.id === selectedId) || null;
  const filtered = functions.filter((f) =>
    (envFilter === 'all' || f.environment?.appName === envFilter) &&
    (statusFilter === 'all' || (statusFilter === 'active' ? f.isActive : !f.isActive)) &&
    (typeFilter === 'all' || f.functionType === typeFilter) &&
    (domainFilter === 'all' || f.functionDomain === domainFilter) &&
    (`${f.functionName} ${f.functionDescription} ${f.functionProgram}`.toLowerCase().includes(search.toLowerCase()))
  );

  const flash = (msg) => { setToast(msg); window.setTimeout(() => setToast(''), 1800); };

  const openCreate = () => { setForm(EMPTY_FORM); setFormTab('base'); setShowCreate(true); };
  const submitCreate = () => {
    if (!form.functionName || !form.environmentName) return;
    const id = Math.max(100, ...functions.map((f) => f.id)) + 1;
    const created = {
      ...form, id, priority: parseInt(form.priority, 10) || 3,
      environment: form.environmentName ? { appName: form.environmentName, appType: 'GENERO_GDC', appVersion: 'v11' } : null,
      md5Key: 'demo' + id, createdBy: 'vous', createdDate: '2026-06-16', modifiedBy: 'vous', modifiedDate: '2026-06-16', lastExecutionDate: null,
    };
    setFunctions((prev) => [created, ...prev]);
    setSelectedId(id); setShowCreate(false); flash('Fonction créée');
  };
  const removeFunction = (id) => {
    setFunctions((prev) => prev.filter((f) => f.id !== id));
    if (selectedId === id) setSelectedId(null);
    flash('Fonction supprimée');
  };
  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e && e.target ? e.target.value : e }));

  // -- Petit filtre <select> stylé (réplique des FilterSelect de l'écran) -----
  const FilterSelect = ({ value, onChange, children }) => (
    <select value={value} onChange={(e) => onChange(e.target.value)}
      className="px-3 py-2 text-xs font-medium rounded-xl bg-gray-100 dark:bg-gray-700 text-gray-700 dark:text-gray-300 border-0 cursor-pointer focus:outline-none focus:ring-2 focus:ring-blue-500/30">
      {children}
    </select>
  );

  return (
    <div className="space-y-8">
      {/* ===== (1) Intro ===== */}
      <div>
        <div className="flex items-center gap-2 flex-wrap">
          <h1 className="text-2xl font-bold text-gray-900 dark:text-white">Fonctions</h1>
          <Badge color="#3B82F6">Module Environnement</Badge>
          <Badge color="#F59E0B">POC interactif</Badge>
        </div>
        <p className="mt-2 text-gray-600 dark:text-gray-300">
          Aperçu interactif de l'écran <strong>Fonctions</strong> (démo, actions simulées localement).
        </p>
      </div>

      {/* ===== (2) Aperçu interactif de l'IHM ===== */}
      <Card className="overflow-hidden">
        {/* En-tête dégradé : icône encadrée + titre + compteur ; à droite bascule vue / rafraîchir / créer */}
        <div className="px-6 py-5 bg-gradient-to-r from-blue-500 to-indigo-700 text-white flex items-center justify-between flex-wrap gap-3">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-xl bg-white/20 flex items-center justify-center"><Code className="h-5 w-5" /></div>
            <div>
              <h2 className="text-2xl font-bold leading-tight">Fonctions</h2>
              <p className="text-sm text-blue-200">{functions.length} fonction{functions.length > 1 ? 's' : ''}</p>
            </div>
          </div>
          <div className="flex items-center gap-2">
            <div className="flex bg-white/10 rounded-xl p-1">
              <button onClick={() => setView('table')} className={`p-2 rounded-lg transition-all ${view === 'table' ? 'bg-white/20 text-white' : 'text-white/60 hover:text-white'}`}><List className="h-4 w-4" /></button>
              <button onClick={() => setView('grid')} className={`p-2 rounded-lg transition-all ${view === 'grid' ? 'bg-white/20 text-white' : 'text-white/60 hover:text-white'}`}><Grid className="h-4 w-4" /></button>
            </div>
            <button onClick={() => flash('Liste rafraîchie')} className="p-2 rounded-xl bg-white/10 hover:bg-white/20 text-white transition-colors"><RefreshCw className="h-[18px] w-[18px]" /></button>
            <button onClick={openCreate} className="flex items-center gap-2 px-4 py-2 rounded-xl bg-white/20 hover:bg-white/30 text-white text-sm font-medium transition-colors"><Plus className="h-[18px] w-[18px]" />Nouvelle fonction</button>
          </div>
        </div>

        {/* --- Barre recherche + filtres (Environnement, Statut, Type, Domaine) --- */}
        <div className="px-6 py-3 border-b border-gray-200 dark:border-gray-700 flex flex-wrap items-center gap-3">
          <div className="flex-1 min-w-[200px] max-w-xs">
            <SearchBar value={search} onChange={setSearch} placeholder="Rechercher..." />
          </div>
          <FilterSelect value={envFilter} onChange={setEnvFilter}>
            <option value="all">Tous les environnements</option>
            {ENVIRONMENTS.map((e) => <option key={e} value={e}>{e}</option>)}
          </FilterSelect>
          <FilterSelect value={statusFilter} onChange={setStatusFilter}>
            <option value="all">Tous statuts</option>
            <option value="active">Actif</option>
            <option value="inactive">Inactif</option>
          </FilterSelect>
          <FilterSelect value={typeFilter} onChange={setTypeFilter}>
            <option value="all">Tous types</option>
            {TYPES.map((t) => <option key={t} value={t}>{t}</option>)}
          </FilterSelect>
          <FilterSelect value={domainFilter} onChange={setDomainFilter}>
            <option value="all">Tous domaines</option>
            {DOMAINS.map((d) => <option key={d} value={d}>{d}</option>)}
          </FilterSelect>
          {toast && <Badge color="#10B981"><CheckCircle className="h-3 w-3" />{toast}</Badge>}
        </div>

        {/* Corps : liste (table/grille) à gauche, tiroir de détail à droite */}
        <div className="flex flex-col lg:flex-row">
          {/* Panneau principal : table ou grille */}
          <div className="flex-1 min-w-0 p-4">
            {filtered.length === 0 ? (
              <EmptyState icon={Code} title="Aucune fonction trouvée" message="Modifiez vos filtres ou créez une nouvelle fonction." />
            ) : view === 'table' ? (
              <div className="overflow-x-auto rounded-lg border border-gray-200 dark:border-gray-700">
                <table className="w-full text-sm">
                  <thead className="bg-gray-50 dark:bg-gray-800 text-gray-500 dark:text-gray-300">
                    <tr>
                      {['#', 'Nom', 'Type', 'Domaine', 'Programme', 'Environnement', 'Priorité', 'Statut', 'Actions'].map((h, i) => (
                        <th key={i} className="text-left text-xs font-semibold uppercase tracking-wider px-3 py-2.5 whitespace-nowrap">{h}</th>
                      ))}
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-gray-100 dark:divide-gray-700/50 text-gray-700 dark:text-gray-200">
                    {filtered.map((f) => (
                      <tr key={f.id} onClick={() => setSelectedId(f.id)}
                        className={`group cursor-pointer transition-colors ${selectedId === f.id ? 'bg-blue-50 dark:bg-blue-500/10' : 'hover:bg-blue-50/50 dark:hover:bg-blue-950/20'}`}>
                        <td className="px-3 py-2.5"><span className="text-xs text-gray-400 font-mono">{f.id}</span></td>
                        <td className="px-3 py-2.5">
                          <div className="flex items-center gap-1.5 font-medium text-gray-900 dark:text-white">{f.functionName}{f.usedByClient && <Star className="h-3.5 w-3.5 text-amber-400 fill-amber-400" />}</div>
                          {f.functionDescription && <div className="text-xs text-gray-500 dark:text-gray-400 truncate max-w-[220px] mt-0.5">{f.functionDescription}</div>}
                        </td>
                        <td className="px-3 py-2.5">{f.functionType ? <Badge color="#8B5CF6">{f.functionType}</Badge> : <span className="text-gray-300">-</span>}</td>
                        <td className="px-3 py-2.5">{f.functionDomain ? <Badge color="#14B8A6">{f.functionDomain}</Badge> : <span className="text-gray-300">-</span>}</td>
                        <td className="px-3 py-2.5"><span className="text-xs text-gray-600 dark:text-gray-400">{f.functionProgram || '-'}</span></td>
                        <td className="px-3 py-2.5">{f.environment ? <Badge color="#3B82F6">{f.environment.appName}</Badge> : <span className="text-gray-300">-</span>}</td>
                        <td className="px-3 py-2.5">{typeof f.priority === 'number' ? <Badge color={priorityColor(f.priority)}>{f.priority}</Badge> : <span className="text-gray-300">-</span>}</td>
                        <td className="px-3 py-2.5">{f.isActive ? <Badge color="#10B981"><CheckCircle className="h-3 w-3" />Actif</Badge> : <Badge color="#EF4444"><XCircle className="h-3 w-3" />Inactif</Badge>}</td>
                        <td className="px-3 py-2.5">
                          <div className="flex items-center justify-center gap-1 opacity-0 group-hover:opacity-100 transition-opacity">
                            <button onClick={(e) => { e.stopPropagation(); flash('Édition simulée'); }} className="p-1.5 rounded-lg text-gray-400 hover:text-blue-500 hover:bg-blue-50 dark:hover:bg-blue-900/20"><Edit2 className="h-[15px] w-[15px]" /></button>
                            <button onClick={(e) => { e.stopPropagation(); removeFunction(f.id); }} className="p-1.5 rounded-lg text-gray-400 hover:text-red-500 hover:bg-red-50 dark:hover:bg-red-900/20"><Trash2 className="h-[15px] w-[15px]" /></button>
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            ) : (
              <div className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-3 gap-3">
                {filtered.map((f) => (
                  <div key={f.id} onClick={() => setSelectedId(f.id)}
                    className={`group cursor-pointer rounded-xl border p-4 transition-all ${selectedId === f.id ? 'border-blue-500 bg-blue-50 dark:bg-blue-500/10' : 'border-gray-200/60 dark:border-gray-700/50 bg-white/70 dark:bg-gray-800/50 hover:shadow-lg hover:border-gray-300 dark:hover:border-gray-600'}`}>
                    {/* Haut : nom + étoile + description à gauche ; pastille de statut à droite */}
                    <div className="flex items-start justify-between mb-3">
                      <div className="min-w-0 flex-1">
                        <div className="flex items-center gap-2">
                          <span className="text-sm font-semibold text-gray-900 dark:text-white truncate">{f.functionName}</span>
                          {f.usedByClient && <Star className="h-3.5 w-3.5 text-amber-400 fill-amber-400 shrink-0" />}
                        </div>
                        {f.functionDescription && <p className="text-xs text-gray-500 dark:text-gray-400 mt-1 line-clamp-2">{f.functionDescription}</p>}
                      </div>
                      <StatusDot active={f.isActive} />
                    </div>
                    {/* Badges : Type / Domaine / Environnement */}
                    <div className="flex flex-wrap gap-1.5 mb-3">
                      {f.functionType && <Badge color="#8B5CF6">{f.functionType}</Badge>}
                      {f.functionDomain && <Badge color="#14B8A6">{f.functionDomain}</Badge>}
                      {f.environment && <Badge color="#3B82F6">{f.environment.appName}</Badge>}
                    </div>
                    {/* Pied : Programme / Module à gauche ; actions Modifier / Supprimer à droite */}
                    <div className="flex items-center justify-between pt-2.5 border-t border-gray-100 dark:border-gray-700">
                      <div className="flex items-center gap-3">
                        {f.functionProgram && <span className="flex items-center gap-1 text-[11px] text-gray-500"><FileCode className="h-3 w-3" />{f.functionProgram}</span>}
                        {f.functionModule && <span className="flex items-center gap-1 text-[11px] text-gray-500"><Package className="h-3 w-3" />{f.functionModule}</span>}
                      </div>
                      <div className="flex gap-1">
                        <button onClick={(e) => { e.stopPropagation(); flash('Édition simulée'); }} className="p-1 rounded text-gray-400 hover:text-blue-500"><Edit2 className="h-3.5 w-3.5" /></button>
                        <button onClick={(e) => { e.stopPropagation(); removeFunction(f.id); }} className="p-1 rounded text-gray-400 hover:text-red-500"><Trash2 className="h-3.5 w-3.5" /></button>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>

          {/* Tiroir de détail (à droite) — sections empilées : StatCards, Détails techniques, badges, Environnement, MD5, Métadonnées */}
          {selected && (
            <div className="lg:w-[440px] shrink-0 border-t lg:border-t-0 lg:border-l border-gray-200 dark:border-gray-700 flex flex-col">
              {/* En-tête tiroir : icône dégradée teal/cyan + nom + étoile + description + fermeture */}
              <div className="p-5 border-b border-gray-100 dark:border-gray-700 bg-gradient-to-r from-teal-50 to-cyan-50 dark:from-gray-800 dark:to-gray-900">
                <div className="flex items-start justify-between">
                  <div className="flex items-center gap-3 min-w-0">
                    <div className="w-11 h-11 rounded-2xl bg-gradient-to-br from-teal-400 to-cyan-600 flex items-center justify-center text-white shadow-lg shadow-teal-500/20"><Code className="h-5 w-5" /></div>
                    <div className="min-w-0">
                      <div className="flex items-center gap-2">
                        <h3 className="text-lg font-bold text-gray-900 dark:text-white truncate">{selected.functionName}</h3>
                        {selected.usedByClient && <Star className="h-4 w-4 text-amber-400 fill-amber-400 shrink-0" />}
                      </div>
                      <p className="text-xs text-gray-500 dark:text-gray-400 mt-0.5">{selected.functionDescription || 'Aucune description'}</p>
                    </div>
                  </div>
                  <button onClick={() => setSelectedId(null)} className="p-1.5 rounded-lg text-gray-400 hover:text-gray-600 hover:bg-gray-100 dark:hover:bg-gray-700"><X className="h-[18px] w-[18px]" /></button>
                </div>
              </div>

              <div className="p-5 space-y-5">
                {/* StatCards 2×2 : ID / Priorité / Niveau / Statut */}
                <div className="grid grid-cols-2 gap-2.5">
                  <StatCard icon={Hash} label="ID" value={selected.id} />
                  <StatCard icon={Zap} label="Priorité" value={selected.priority} />
                  <StatCard icon={Layers} label="Niveau" value={levelLabel(selected.level)} />
                  <StatCard icon={Shield} label="Statut" value={selected.isActive ? 'Actif' : 'Inactif'} />
                </div>

                {/* Carte Détails techniques */}
                <DetailCard title="Détails techniques" rows={[
                  [FileCode, 'Programme', selected.functionProgram],
                  [Package, 'Module', selected.functionModule],
                  [Tag, 'Type', selected.functionType],
                  [Globe, 'Domaine', selected.functionDomain],
                ]} />

                {/* Ligne de badges : Actif/Inactif + Spécifique + Client */}
                <div className="flex flex-wrap items-center gap-2">
                  {selected.isActive ? <Badge color="#10B981"><CheckCircle className="h-3 w-3" />Actif</Badge> : <Badge color="#EF4444"><XCircle className="h-3 w-3" />Inactif</Badge>}
                  {selected.isSpecific && <Badge color="#8B5CF6">Spécifique</Badge>}
                  {selected.usedByClient && <Badge color="#F59E0B"><Star className="h-3 w-3" />Client</Badge>}
                </div>

                {/* Callout Environnement (bleu) */}
                {selected.environment && (
                  <div className="rounded-xl border border-blue-200 dark:border-blue-800/30 bg-blue-50/50 dark:bg-blue-900/10 p-3.5">
                    <div className="flex items-center gap-2 mb-2">
                      <Server className="h-3.5 w-3.5 text-blue-500" />
                      <span className="text-xs font-semibold text-blue-700 dark:text-blue-400">{selected.environment.appName}</span>
                    </div>
                    <div className="flex gap-4">
                      <span className="text-[11px] text-gray-500">Type : {selected.environment.appType || '-'}</span>
                      <span className="text-[11px] text-gray-500">Version : {selected.environment.appVersion || '-'}</span>
                    </div>
                  </div>
                )}

                {/* Ligne MD5 */}
                {selected.md5Key && (
                  <div className="px-3 py-2 rounded-lg bg-gray-50 dark:bg-gray-800">
                    <span className="text-[10px] text-gray-400">MD5 : </span>
                    <span className="text-[10px] font-mono text-gray-500">{selected.md5Key}</span>
                  </div>
                )}

                {/* Carte Métadonnées */}
                <DetailCard title="Métadonnées" rows={[
                  [User, 'Créé par', selected.createdBy],
                  [CalendarDays, 'Date création', selected.createdDate],
                  [User, 'Modifié par', selected.modifiedBy],
                  [Clock, 'Date modification', selected.modifiedDate],
                  [Clock, 'Dernière exécution', selected.lastExecutionDate || 'Jamais'],
                ]} />
              </div>

              {/* Pied du tiroir : Test IA / Modifier / Voir les tâches */}
              <div className="mt-auto p-4 border-t border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800 flex items-center gap-2">
                <button onClick={() => flash('Test IA simulé')} className="flex items-center gap-1.5 px-3 py-2 text-xs font-medium text-white bg-gradient-to-r from-violet-500 to-purple-600 rounded-lg hover:from-violet-600 hover:to-purple-700 shadow-sm"><Brain className="h-3.5 w-3.5" />Test IA</button>
                <button onClick={() => flash('Édition simulée')} className="flex items-center gap-1.5 px-3 py-2 text-xs font-medium text-gray-600 bg-gray-100 rounded-lg hover:bg-gray-200 dark:bg-gray-700 dark:text-gray-300 dark:hover:bg-gray-600"><Edit2 className="h-3.5 w-3.5" />Modifier</button>
                <button onClick={() => flash('Navigation vers les tâches')} className="flex items-center gap-1.5 px-3 py-2 text-xs font-medium text-white bg-blue-500 rounded-lg hover:bg-blue-600 ml-auto">Voir les tâches<ExternalLink className="h-3 w-3" /></button>
              </div>
            </div>
          )}
        </div>
      </Card>

      {/* ===== Formulaire de création — Dialog modal à 2 onglets ===== */}
      {showCreate && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/40" onClick={() => setShowCreate(false)}>
          <div className="w-full max-w-2xl rounded-2xl bg-white dark:bg-gray-800 shadow-xl" onClick={(e) => e.stopPropagation()}>
            <div className="flex items-center justify-between px-5 py-4 border-b border-gray-200 dark:border-gray-700">
              <h3 className="text-lg font-bold text-gray-900 dark:text-white flex items-center gap-2"><Plus className="h-5 w-5" />Nouvelle fonction</h3>
              <button onClick={() => setShowCreate(false)} className="text-gray-400 hover:text-gray-600"><X className="h-5 w-5" /></button>
            </div>
            <div className="p-5">
              <Tabs tabs={[{ id: 'base', label: 'Informations de base' }, { id: 'advanced', label: 'Propriétés avancées' }]} active={formTab} onChange={setFormTab} className="mb-4" />

              {formTab === 'base' ? (
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  <label className="text-sm">
                    <span className="block text-gray-700 dark:text-gray-300 mb-1">Nom <span className="text-red-500">*</span></span>
                    <input value={form.functionName} onChange={set('functionName')} placeholder="Saisie virement" className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-800 text-gray-800 dark:text-gray-100 rounded-md focus:ring-2 focus:ring-blue-500" />
                  </label>
                  <label className="text-sm">
                    <span className="block text-gray-700 dark:text-gray-300 mb-1">Environnement <span className="text-red-500">*</span></span>
                    <select value={form.environmentName} onChange={set('environmentName')} className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-800 text-gray-800 dark:text-gray-100 rounded-md focus:ring-2 focus:ring-blue-500">
                      <option value="">— Choisir —</option>
                      {ENVIRONMENTS.map((e) => <option key={e} value={e}>{e}</option>)}
                    </select>
                  </label>
                  <label className="text-sm sm:col-span-2">
                    <span className="block text-gray-700 dark:text-gray-300 mb-1">Description</span>
                    <textarea value={form.functionDescription} onChange={set('functionDescription')} rows={2} className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-800 text-gray-800 dark:text-gray-100 rounded-md focus:ring-2 focus:ring-blue-500" />
                  </label>
                  <label className="text-sm">
                    <span className="block text-gray-700 dark:text-gray-300 mb-1">Programme</span>
                    <input value={form.functionProgram} onChange={set('functionProgram')} placeholder="VIRSAISIE" className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-800 text-gray-800 dark:text-gray-100 rounded-md focus:ring-2 focus:ring-blue-500" />
                  </label>
                  <label className="text-sm">
                    <span className="block text-gray-700 dark:text-gray-300 mb-1">Module</span>
                    <input value={form.functionModule} onChange={set('functionModule')} placeholder="VIREMENTS" className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-800 text-gray-800 dark:text-gray-100 rounded-md focus:ring-2 focus:ring-blue-500" />
                  </label>
                  <label className="text-sm">
                    <span className="block text-gray-700 dark:text-gray-300 mb-1">Type</span>
                    <input value={form.functionType} onChange={set('functionType')} placeholder="PROGRAM" className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-800 text-gray-800 dark:text-gray-100 rounded-md focus:ring-2 focus:ring-blue-500" />
                  </label>
                  <label className="text-sm">
                    <span className="block text-gray-700 dark:text-gray-300 mb-1">Domaine</span>
                    <input value={form.functionDomain} onChange={set('functionDomain')} placeholder="CREDIT" className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-800 text-gray-800 dark:text-gray-100 rounded-md focus:ring-2 focus:ring-blue-500" />
                  </label>
                </div>
              ) : (
                <div className="space-y-4">
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                    <label className="text-sm">
                      <span className="block text-gray-700 dark:text-gray-300 mb-1">Priorité</span>
                      <select value={form.priority} onChange={set('priority')} className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-800 text-gray-800 dark:text-gray-100 rounded-md focus:ring-2 focus:ring-blue-500">
                        <option value={1}>1 - Très basse</option>
                        <option value={2}>2 - Basse</option>
                        <option value={3}>3 - Moyenne</option>
                        <option value={4}>4 - Haute</option>
                        <option value={5}>5 - Très haute</option>
                      </select>
                    </label>
                    <label className="text-sm">
                      <span className="block text-gray-700 dark:text-gray-300 mb-1">Niveau</span>
                      <select value={form.level} onChange={set('level')} className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-800 text-gray-800 dark:text-gray-100 rounded-md focus:ring-2 focus:ring-blue-500">
                        {LEVELS.map((l) => <option key={l.value} value={l.value}>{l.label}</option>)}
                      </select>
                    </label>
                  </div>
                  <div className="flex flex-wrap items-center gap-6">
                    <label className="flex items-center gap-2 text-sm text-gray-700 dark:text-gray-300">
                      <input type="checkbox" checked={form.isActive} onChange={(e) => setForm((f) => ({ ...f, isActive: e.target.checked }))} className="h-4 w-4" />Actif
                    </label>
                    <label className="flex items-center gap-2 text-sm text-gray-700 dark:text-gray-300">
                      <input type="checkbox" checked={form.isSpecific} onChange={(e) => setForm((f) => ({ ...f, isSpecific: e.target.checked }))} className="h-4 w-4" />Spécifique
                    </label>
                    <label className="flex items-center gap-2 text-sm text-gray-700 dark:text-gray-300">
                      <input type="checkbox" checked={form.usedByClient} onChange={(e) => setForm((f) => ({ ...f, usedByClient: e.target.checked }))} className="h-4 w-4" />Utilisée par le client
                    </label>
                  </div>
                </div>
              )}
            </div>
            <div className="flex justify-end gap-2 px-5 py-4 border-t border-gray-200 dark:border-gray-700">
              <Button variant="ghost" onClick={() => setShowCreate(false)}>Annuler</Button>
              <Button onClick={submitCreate} disabled={!form.functionName || !form.environmentName}><Save className="h-4 w-4" />Créer</Button>
            </div>
          </div>
        </div>
      )}

      {/* ===== (3) Guide utilisateur ===== */}
      <div>
        <h2 className="text-xl font-bold text-gray-900 dark:text-white mb-4">Guide utilisateur</h2>
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
          <Card><CardContent>
            <h3 className="font-semibold text-gray-900 dark:text-white flex items-center gap-2 mb-3"><Plus className="h-5 w-5 text-blue-500" />Créer une fonction</h3>
            <ol className="space-y-2">
              <GuideStep n={1}>Cliquer sur <strong>« Nouvelle fonction »</strong> (en haut à droite). <button onClick={openCreate} className="text-blue-600 dark:text-blue-400 underline">Essayer</button></GuideStep>
              <GuideStep n={2}>Onglet <strong>Informations de base</strong> : saisir le <strong>Nom</strong> et choisir l'<strong>Environnement</strong> (obligatoires).</GuideStep>
              <GuideStep n={3}>Renseigner Programme, Module, Type et Domaine si connus.</GuideStep>
              <GuideStep n={4}>Onglet <strong>Propriétés avancées</strong> : ajuster Priorité, Niveau et les indicateurs (Actif, Spécifique, Client).</GuideStep>
              <GuideStep n={5}>Cliquer sur <strong>Créer</strong> — la fonction apparaît en tête de liste.</GuideStep>
            </ol>
          </CardContent></Card>

          <Card><CardContent>
            <h3 className="font-semibold text-gray-900 dark:text-white flex items-center gap-2 mb-3"><Info className="h-5 w-5 text-blue-500" />Consulter le détail</h3>
            <ol className="space-y-2">
              <GuideStep n={1}>Cliquer sur une ligne (ou une carte) pour ouvrir le <strong>tiroir de détail</strong>. <button onClick={() => { setShowCreate(false); setSelectedId(SAMPLE[0].id); }} className="text-blue-600 dark:text-blue-400 underline">Essayer</button></GuideStep>
              <GuideStep n={2}>Lire les <strong>indicateurs</strong> (ID, Priorité, Niveau, Statut) et les <strong>Détails techniques</strong> (Programme, Module, Type, Domaine).</GuideStep>
              <GuideStep n={3}>Plus bas : <strong>Environnement</strong> rattaché, clé <strong>MD5</strong> et <strong>Métadonnées</strong> (créateur, dates, dernière exécution).</GuideStep>
              <GuideStep n={4}>Actions du tiroir : <strong>Test IA</strong>, <strong>Modifier</strong>, accès aux <strong>Tâches</strong> liées.</GuideStep>
            </ol>
          </CardContent></Card>

          <Card><CardContent>
            <h3 className="font-semibold text-gray-900 dark:text-white flex items-center gap-2 mb-3"><Server className="h-5 w-5 text-blue-500" />Rechercher & filtrer</h3>
            <ol className="space-y-2">
              <GuideStep n={1}>Utiliser la <strong>barre de recherche</strong> (nom, description, programme).</GuideStep>
              <GuideStep n={2}>Filtrer par <strong>Environnement</strong>, <strong>Statut</strong>, <strong>Type</strong> ou <strong>Domaine</strong>. <button onClick={() => { setShowCreate(false); setStatusFilter('active'); }} className="text-blue-600 dark:text-blue-400 underline">Actifs seulement</button></GuideStep>
              <GuideStep n={3}>Basculer entre la vue <strong>table</strong> et la vue <strong>grille</strong>. <button onClick={() => { setShowCreate(false); setView('grid'); }} className="text-blue-600 dark:text-blue-400 underline">Vue grille</button></GuideStep>
              <GuideStep n={4}>Les fonctions <strong>« utilisées par le client »</strong> portent une étoile.</GuideStep>
            </ol>
          </CardContent></Card>
        </div>
      </div>

      {/* ===== (4) Référence des champs ===== */}
      <Card><CardContent>
        <h3 className="font-semibold text-gray-900 dark:text-white flex items-center gap-2 mb-3"><Info className="h-5 w-5 text-blue-500" />Référence des champs</h3>
        <div className="overflow-x-auto rounded-lg border border-gray-200 dark:border-gray-700">
          <table className="w-full text-sm">
            <thead className="bg-gray-50 dark:bg-gray-800 text-gray-600 dark:text-gray-300">
              <tr>{['Champ', 'Type', 'Requis', 'Détails'].map((h) => <th key={h} className="text-left font-semibold px-4 py-2">{h}</th>)}</tr>
            </thead>
            <tbody className="divide-y divide-gray-100 dark:divide-gray-800 text-gray-700 dark:text-gray-200">
              {[
                ['Nom (functionName)', 'Texte', 'Oui', 'Libellé de la fonction'],
                ['Environnement', 'Liste', 'Oui', 'Application cible rattachée'],
                ['Description', 'Texte long', 'Non', 'Documentation libre'],
                ['Programme (functionProgram)', 'Texte', 'Non', 'Identifiant programme (alimenté par l\'import agent)'],
                ['Module (functionModule)', 'Texte', 'Non', 'Regroupement fonctionnel'],
                ['Type (functionType)', 'Texte', 'Non', 'Ex. PROGRAM, MENU, FUNCTION, REPORT, BATCH, SERVICE, API, WEB, INCLUDE, LIBRARY'],
                ['Domaine (functionDomain)', 'Texte', 'Non', 'Valeurs distinctes en base (ex. CREDIT, DEBIT…)'],
                ['Niveau (level)', 'Liste', 'Non', 'Basique / Intermédiaire / Avancé / Expert'],
                ['Priorité (priority)', 'Nombre', 'Non', 'De 1 (très basse) à 5 (très haute) — défaut 3'],
                ['Actif (isActive)', 'Interrupteur', 'Non', 'Actif par défaut'],
                ['Spécifique (isSpecific)', 'Interrupteur', 'Non', 'Implémentation sur-mesure'],
                ['Utilisée par le client (usedByClient)', 'Interrupteur', 'Non', 'Affiche une étoile dans la liste'],
              ].map((r, i) => (
                <tr key={i} className="hover:bg-gray-50 dark:hover:bg-gray-800/50">
                  <td className="px-4 py-2 font-medium">{r[0]}</td><td className="px-4 py-2">{r[1]}</td>
                  <td className="px-4 py-2">{r[2] === 'Oui' ? <Badge color="#EF4444">Oui</Badge> : <Badge>Non</Badge>}</td>
                  <td className="px-4 py-2 text-gray-600 dark:text-gray-300">{r[3]}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </CardContent></Card>

      {/* ===== Règles de gestion + Endpoints API ===== */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
        <Card><CardContent>
          <h3 className="font-semibold text-gray-900 dark:text-white mb-3">Règles de gestion</h3>
          <ul className="space-y-2 text-sm text-gray-700 dark:text-gray-300">
            <li className="flex items-start gap-2"><CheckCircle className="h-4 w-4 text-green-500 mt-0.5 shrink-0" />Le <strong>Nom</strong> et l'<strong>Environnement</strong> sont obligatoires à la création.</li>
            <li className="flex items-start gap-2"><CheckCircle className="h-4 w-4 text-green-500 mt-0.5 shrink-0" />À la modification, les champs <strong>Créé par</strong> et <strong>Date de création</strong> sont préservés ; <strong>Date de modification</strong> est mise à jour automatiquement.</li>
            <li className="flex items-start gap-2"><CheckCircle className="h-4 w-4 text-green-500 mt-0.5 shrink-0" />L'<strong>import depuis l'agent</strong> déduplique par <strong>clé MD5</strong> puis par couple Programme + Environnement (créé / mis à jour / ignoré).</li>
            <li className="flex items-start gap-2"><XCircle className="h-4 w-4 text-red-500 mt-0.5 shrink-0" />La <strong>suppression</strong> d'une fonction entraîne en cascade ses Tâches, Évolutions et Anomalies liées.</li>
          </ul>
        </CardContent></Card>

        <Card><CardContent>
          <h3 className="font-semibold text-gray-900 dark:text-white mb-3">Endpoints API</h3>
          <ul className="space-y-1.5 text-sm font-mono text-gray-700 dark:text-gray-200">
            {[
              ['GET', '#10B981', '/api/automata/functions'],
              ['GET', '#10B981', '/api/automata/functions/page'],
              ['GET', '#10B981', '/api/automata/functions/{id}'],
              ['POST', '#3B82F6', '/api/automata/functions'],
              ['PUT', '#F59E0B', '/api/automata/functions/{id}'],
              ['DELETE', '#EF4444', '/api/automata/functions/{id}'],
              ['POST', '#3B82F6', '/api/automata/functions/import-from-agent/{environmentId}'],
            ].map((r, i) => (
              <li key={i} className="flex items-center gap-2"><Badge color={r[1]}>{r[0]}</Badge><span className="break-all">{r[2]}</span></li>
            ))}
          </ul>
          <p className="text-xs text-gray-400 mt-3 flex items-center gap-1"><ArrowRight className="h-3 w-3" />Permissions : FUNCTION_VIEW / FUNCTION_CREATE / FUNCTION_UPDATE / FUNCTION_DELETE — Source : automata_environment / FunctionController</p>
        </CardContent></Card>
      </div>
    </div>
  );
};

export default FunctionsPOC;
